/**
 * @file src/core/events.js
 * Emitter thuộc về ctx của TỪNG tiết.
 *
 * Vì sao không dùng window.dispatchEvent (như three-lessons đang làm):
 *  - global → hai tiết cùng nghe sẽ chồng nhau khi đổi tiết
 *  - không dispose được → listener sống mãi
 *  - không namespace → dễ trùng tên với thư viện khác
 */

/**
 * @typedef {Object} Emitter
 * @property {(type: string, handler: (payload: any) => void) => () => void} on   Trả về hàm unsubscribe
 * @property {(type: string, handler: (payload: any) => void) => () => void} once
 * @property {(type: string, payload?: any) => void} emit
 * @property {() => void} clear
 * @property {() => string[]} types  Debug: đang nghe những gì
 */

/** @returns {Emitter} */
export function createEmitter() {
  /** @type {Map<string, Set<(p: any) => void>>} */
  const map = new Map();

  /** @param {string} type @param {(p: any) => void} handler @returns {() => void} */
  function on(type, handler) {
    let set = map.get(type);
    if (!set) {
      set = new Set();
      map.set(type, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
      if (set && set.size === 0) map.delete(type);
    };
  }

  return {
    on,

    once(type, handler) {
      const off = on(type, (p) => {
        off();
        handler(p);
      });
      return off;
    },

    emit(type, payload) {
      const set = map.get(type);
      if (!set) return;
      // copy để handler tự gỡ mình không làm vỡ vòng lặp
      for (const h of [...set]) {
        try {
          h(payload);
        } catch (e) {
          console.error(`[events] handler "${type}" lỗi:`, e);
        }
      }
    },

    clear() {
      map.clear();
    },

    types() {
      return [...map.keys()];
    },
  };
}
